import type {
  AiMonthlyUsage,
  AiSettings,
  AiSpendStatus,
  AiUsageLogEntry,
} from "../types";

const BUSINESS_TIME_ZONE = "America/Mexico_City";

/**
 * First day of the calendar month (America/Mexico_City) of `date`, as
 * `YYYY-MM-DD` -- the same bucketing as the `ai_usage_monthly` view.
 */
export const monthKeyOf = (date: Date | string): string => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BUSINESS_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date(date));
  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  return `${year}-${month}-01`;
};

/** Frontend emulation of the `ai_usage_monthly` view (group by month, action). */
export const rollupAiUsage = (
  log: readonly AiUsageLogEntry[],
): AiMonthlyUsage[] => {
  const groups = new Map<string, AiMonthlyUsage>();
  for (const entry of log) {
    const month = monthKeyOf(entry.created_at);
    const key = `${month}|${entry.action}`;
    const prev = groups.get(key) ?? {
      month,
      action: entry.action,
      calls: 0,
      input_tokens: 0,
      output_tokens: 0,
      estimated_cost_usd: 0,
    };
    groups.set(key, {
      ...prev,
      calls: prev.calls + 1,
      input_tokens: prev.input_tokens + entry.input_tokens,
      output_tokens: prev.output_tokens + entry.output_tokens,
      estimated_cost_usd: prev.estimated_cost_usd + entry.estimated_cost_usd,
    });
  }
  return [...groups.values()].sort(
    (a, b) =>
      b.month.localeCompare(a.month) || a.action.localeCompare(b.action),
  );
};

/** Current-month spend vs the cap, from the monthly rollup rows. */
export const computeSpendStatus = (
  rollup: readonly AiMonthlyUsage[],
  settings: Pick<AiSettings, "monthly_cap_usd">,
  now: Date = new Date(),
): AiSpendStatus => {
  const month = monthKeyOf(now);
  const spentUsd = rollup
    .filter((row) => row.month === month)
    .reduce((sum, row) => sum + Number(row.estimated_cost_usd), 0);
  const capUsd = Number(settings.monthly_cap_usd);
  return {
    month,
    spentUsd,
    capUsd,
    remainingUsd: Math.max(0, capUsd - spentUsd),
  };
};
